import { InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory'
import { ApolloClient } from 'apollo-client'
import { setContext } from 'apollo-link-context'
import { onError } from 'apollo-link-error'
import { HttpLink } from 'apollo-link-http'
import * as React from 'react'
import { ApolloProvider } from 'react-apollo'
import { getToken, logout } from 'web-fns/dist/auth'
import notification from 'web-makeup/dist/Notification'

import * as introspectionQueryResultData from './fragmentTypes.json'

export const ensureEnvVars = (env, window) => {
  if (env.REACT_APP_USE_MOCK_API === 'true') {
    if (!env.REACT_APP_MOCK_API_URL) {
      throw new ReferenceError(
        'env.REACT_APP_MOCK_API_URL must contain a URI when process.env.REACT_APP_USE_MOCK_API is true.'
      )
    }
    if (window.env) {
      window.env.API_URL = env.REACT_APP_MOCK_API_URL
    }
  }

  // Catch runtime error in case `env.js` is missing `API_URL`.
  if (window.env && !window.env.API_URL) {
    throw new ReferenceError('window.env.API_URL must contain a URI.')
  }
}

ensureEnvVars(process.env, window)

const httpLink = new HttpLink({
  uri: window.env && window.env.API_URL,
  credentials: 'include',
})

const ERRORS = {
  '401-INVALID_APP_ID': 'You are using an invalid app id.',
  '401-INVALID_JWT': 'You are using an invalid JWT. Try refresh the app!',
  '401-EXPIRED_JWT': 'Your JWT is expired. The app will refresh to get new token!',
  '500': 'Server exploded, oops.',
  defaults: 'Please try again.',
}

type NError = Error & {
  statusCode?: number
  result?: {
    errors?: Array<{ message: string }>
  }
}

export const onErrorHandler = ({ networkError }: { networkError?: Error }) => {
  if (!networkError) {
    return
  }

  try {
    const netError: NError = networkError

    const errorName = `${netError.statusCode}-${netError.result!.errors![0].message}`

    notification.error({
      message: 'A network error has happened.',
      description: ERRORS[errorName] || ERRORS.defaults,
      duration: 0,
    })

    // If the JWT is invalid, clear it so it will be refreshed if user click refresh
    if (errorName === '401-INVALID_JWT') {
      logout()
    }

    // If the token is expired, refresh the app so it will be renewed
    if (errorName === '401-EXPIRED_JWT') {
      logout()
      location.reload()
    }
  } catch (err) {
    notification.error({
      message: 'A network error has happened.',
      description: ERRORS.defaults,
    })
  }
}

// Try to handle network errors here since network errors
// are likely to be generic.
const errorLink = onError(onErrorHandler)

export const contextHandler = (_, { headers }) => {
  // get the authentication token from local storage if it exists
  const jwt = getToken()

  return {
    headers: {
      ...headers,

      // Add `authorization` only if you are logged in.
      ...(jwt ? { authorization: `Bearer ${jwt}` } : {}),

      // Without this, server-side geoip won't work on local cluster
      // so this is a Singapore-IP for non-production.
      ...(process.env.NODE_ENV === 'production' ? {} : { 'X-Forwarded-For': '101.100.177.178' }),

      'X-APP-ID': window.env && window.env.CLIENT_APP_ID,
    },
  }
}

const fragmentMatcher = new IntrospectionFragmentMatcher({ introspectionQueryResultData })
const cache = new InMemoryCache({ fragmentMatcher })
const headersLink = setContext(contextHandler)

export const client = new ApolloClient({
  link: headersLink.concat(errorLink).concat(httpLink),
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'ignore',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'none',
    },
    mutate: {
      errorPolicy: 'none',
    },
  },
})

interface Props {
  children: React.ReactElement<{}>
}

const GraphQLProvider: React.SFC<Props> = ({ children }: Props) => (
  <ApolloProvider client={client}>{children}</ApolloProvider>
)

export default GraphQLProvider



// WEBPACK FOOTER //
// ./src/components/ApolloProvider/ApolloProvider.tsx